/*
 * Install Base History
 */

import * as React from 'react';

import { ListGroup, ListGroupItem } from 'react-bootstrap';

import { IMPLRwithMeasurementType, IWorkOrder, ImeasurementPointResult, Ijob } from '../../models';
import { TFunction } from 'i18next';
import { measurementPointResultStatusTypesEnum, workOrderTypesEnum } from '../../models-enums';
import moment from 'moment';
import { clearHistoricalResultID, updateMeasurementPointResult } from '../../actions/measurementPointResultsActions';
import { clearSelectedHistoricalWorkOrderID, setSelectedHistoricalWorkOrderID } from '../../actions/workOrderActions';
import { ccWONotes } from '../../reducers/workOrderReducer';
import { isHistoryWorkOrder, jobIDsCoveredByWorkOrderRows, showsHistoryResultRow, showsHistoryWorkOrderRow } from './historyRows';

interface Iprops {
    installBaseHistory: Array<IMPLRwithMeasurementType | IWorkOrder>;
    installBaseID: string;
    t: TFunction;
    updateMeasurementPointResult: typeof updateMeasurementPointResult;
    setSelectedHistoricalWorkOrderID: typeof setSelectedHistoricalWorkOrderID;
    clearHistoricalResultID: typeof clearHistoricalResultID;
    clearSelectedHistoricalWorkOrderID: typeof clearSelectedHistoricalWorkOrderID;
    loading: boolean;
    selectedJob: Ijob;
    /* Which job each work order belongs to - IWorkOrder does not carry it (MMG-4345) */
    jobIDByWorkOrderID: { [workOrderID: string]: string };
    /* ID of the history row currently open in the overlay; '' when nothing is open */
    selectedHistoryID: string;
}

export const MeasurementPointListResultHistory = (props: Iprops) => {
    const { t, selectedHistoryID } = props;
    const handleWorkOrderClick = (wo: IWorkOrder) => {
        props.setSelectedHistoricalWorkOrderID(wo.id);
    };
    const handleMPLRClick = (result: ImeasurementPointResult) => {
        props.updateMeasurementPointResult(result, true);
    };

    /*
     * While a row is open, the rows that are not open are greyed out so the FSE can see which
     * visit they are looking at (MMG-4345). Clearing the overlay - FINISH VIEWING HISTORICAL
     * RESULT - empties selectedHistoryID and every row goes back to normal.
     */
    const rowClassName = (rowID: string) =>
        selectedHistoryID.length && selectedHistoryID !== rowID ? 'history history-dimmed' : 'history';

    const jobTypeID = props.selectedJob.jobTypeID;
    /*
     * The visits already listed as a work order row. Their result row is the same visit again, so
     * it is dropped - but only for these, never on the assumption that a work order is there.
     */
    const jobIDsOnWorkOrderRows = jobIDsCoveredByWorkOrderRows(
        props.installBaseHistory,
        jobTypeID,
        props.jobIDByWorkOrderID
    );

    if (props.installBaseHistory.length) {
        return (
            <div className="result-history">
                <h4>{String(t('historyHeading'))}</h4>
                <ListGroup>
                    {props.installBaseHistory.map(result => {
                        if (isHistoryWorkOrder(result)) {
                            if (!showsHistoryWorkOrderRow(result, jobTypeID)) {
                                return null;
                            }

                            let { closingNotesList } = result as IWorkOrder;

                            // If for some reason the closingNotesList is not set, try to parse it from the closingNotes string
                            if (closingNotesList === null && result.closingNotes) {
                                closingNotesList = ccWONotes(JSON.parse(result.closingNotes));
                            }

                            return (
                                <ListGroupItem
                                    key={result.id}
                                    onClick={() => handleWorkOrderClick(result)}
                                    title={result.closingNotes}
                                    className={rowClassName(result.id)}
                                >
                                    <div className="history-meta">
                                        <span className="history-date">
                                            {moment
                                                .utc(result.closingDate)
                                                .local(true)
                                                .format('DD-MMM-YY LT')}
                                        </span>
                                        <span className={`status ${workOrderTypesEnum[result.type]}`}>
                                            {/* For service plan, print Maintain */}
                                            {result.type === workOrderTypesEnum.servicePlan &&
                                                String(t('manageWorkOrder:' + workOrderTypesEnum[2]))}
                                            {/* For commissioning, print Commission */}
                                            {result.type === workOrderTypesEnum.commissioning &&
                                                String(t('manageWorkOrder:' + workOrderTypesEnum[5]))}
                                            {/* For everything else, print it's matching name */}
                                            {result.type !== workOrderTypesEnum.servicePlan &&
                                                result.type !== workOrderTypesEnum.commissioning &&
                                                String(t('manageWorkOrder:' + workOrderTypesEnum[result.type]))}
                                        </span>
                                    </div>
                                    {closingNotesList && (
                                        <span className="notes-column">
                                            {closingNotesList.map((note, idx: number) => (
                                                <div key={idx}>{note.text}</div>
                                            ))}
                                        </span>
                                    )}
                                </ListGroupItem>
                            );
                        } else if ('measurementPointListID' in result) {
                            if (!showsHistoryResultRow(result, jobTypeID, jobIDsOnWorkOrderRows)) {
                                return null;
                            }

                            return (
                                <ListGroupItem
                                    key={result.id}
                                    onClick={() => handleMPLRClick(result)}
                                    title={result.notes}
                                    className={rowClassName(result.id)}
                                >
                                    <div className="history-meta">
                                        <span className="history-date">
                                            {moment
                                                .utc(result.updateDate)
                                                .local(true)
                                                .format('DD-MMM-YY LT')}
                                        </span>
                                        <span className={`status ${measurementPointResultStatusTypesEnum[result.status]}`}>
                                            {String(
                                                t(
                                                    'manageMeasurementPointLists:' +
                                                        measurementPointResultStatusTypesEnum[result.status]
                                                )
                                            )}
                                        </span>
                                    </div>
                                    {result.notes && (
                                        <span className="notes-column">{result.notes}</span>
                                    )}
                                </ListGroupItem>
                            );
                        } else {
                            return null;
                        }
                    })}
                </ListGroup>
            </div>
        );
    } else if (props.loading) {
        return (
            <div className="result-history">
                <h4>{String(t('historyHeading'))}</h4>
                <p>{String(t('common:loadingText'))}</p>
            </div>
        );
    } else {
        return (
            <div className="result-history">
                <h4>{String(t('historyHeading'))}</h4>
                <p>{String(t('noInspectionMessage'))}</p>
            </div>
        );
    }
};
